import { composeDeviceAttributes } from "@/services/device-attributes/device-attributes";
import type DeviceMapEntity from "@/services/map-management/map-entities/device-map-entity";
import type MapEntityBase from "@/services/map-management/map-entities/map-entity-base";
import {
    makeBadgeParams,
    makeColorParams,
    makeProgressRingParams,
    makeWarnings,
} from "../../../services/map-management/map-entities/shared";
import { FullMarker } from "./components/full-marker";
import { Icon } from "./components/icon/icon";
import { makeIconConfig, markerIconSizeScaling } from "./components/icon/icon-config";
import { menuBadgeSize, menuIconSize } from "./configs/sizes";

interface DeviceMarkerAvatarProps {
    mapEntity: MapEntityBase | DeviceMapEntity;
    size?: number;
    badgeSize?: number;
    withProgressRing?: boolean;
    withBadge?: boolean;
    onClick?: () => void;
}
/**
 * A simplified device marker component that renders a FullIcon for deviceMapItem
 * Used in cards and menus without map-specific functionality
 */
export function DeviceMarkerAvatar({
    mapEntity,
    size = menuIconSize,
    badgeSize = menuBadgeSize,
    withProgressRing = true,
    withBadge = true,
    onClick,
}: DeviceMarkerAvatarProps) {
    const { status } = makeWarnings({
        model: mapEntity.model,
        type: mapEntity.model.type,
    });
    const { operatingMode } = composeDeviceAttributes({ model: mapEntity.model });

    const { variant, iconConfig } = makeIconConfig({
        manualMode: operatingMode.isManualMode,
        deviceType: mapEntity.model.type,
        connectedEquipment: mapEntity.model.connectedEquipment,
    });

    const colorParams = makeColorParams({
        deviceType: mapEntity.model.type,
        notes: mapEntity.model.notes,
    });

    const badgeParams = makeBadgeParams({ warningStatus: status, size: badgeSize });

    return (
        <FullMarker
            onClick={onClick}
            key={mapEntity.id}
            size={size}
            colorParams={colorParams}
            badgeParams={withBadge ? badgeParams : undefined}
            hoverEffect={false}
            progressRingParams={withProgressRing ? makeProgressRingParams({ model: mapEntity.model }) : undefined}
        >
            <Icon color={colorParams.iconColor} size={size * markerIconSizeScaling[variant]} {...iconConfig} />
        </FullMarker>
    );
}
